import React, { useEffect, useState } from 'react'
import { Mutation, Query } from 'react-apollo'
import CardLayout from 'components/CardLayout'
import Form from 'react-jsonschema-form-bs4'
import LayoutField from 'react-jsonschema-form-layout-grid'
import { injectIntl } from 'react-intl'
import { compose, evolve, find, flatten, groupBy, map, mapObjIndexed, path, pathEq, prop, values, includes } from 'ramda'
import { connect } from 'react-redux'
import { checkProfileStatus } from 'actions'
import { GraphqlError } from 'util/GraphqlError'
import { LoadingView } from '../Loading/Loading'
import { FieldTemplate, TitleField, validate } from './formSpecialisation'
import { addOrUpdate, query } from './queries'
import { getSchema, uiSchema } from './formConfiguration'
import { setSpecialistDetailsStep } from 'actions/Components'
import { getConfiguredClient } from 'util/graphqlClient'
import { withRouter } from 'react-router'
import { debug as dbg } from 'debug'
const debug = dbg('linkgua:specialist:details')


export const validSteps = {
  personal: 'personal',
  languages: 'languages',
  terms: 'terms',
  company: 'company',
}

export const getTermsList = (intl) => [
  intl.formatMessage({id: 'component.specialistDetails.terms.general'}),
]


const makeNullsUndefined = map(value => value === null ? undefined : value)

const formatSpecialistForForm = (specialist, intl) => {
  let data = makeNullsUndefined(specialist)
  const languageKnowledgesBySourceLanguage = groupBy(
    prop('sourceLanguage'),
    data.languageKnowledges
  )

  const formatGroupedLanguageKnowledgesForForm = (workAreas, sourceLanguage) => {
    return ({
      sourceLanguage,
      workArea: map(path(['workArea', 'id']), workAreas),
      originalWorkAreas: workAreas
    })
  }

  data.languageKnowledges = values(mapObjIndexed(
    formatGroupedLanguageKnowledgesForForm,
    languageKnowledgesBySourceLanguage
  ))

  data.terms = specialist.termsAccepted ? getTermsList(intl) : []

  return data
}

const formatLanguageKnowledge = (languageKnowledge) =>
  map(
    (workArea) => {
      let originalWorkArea = undefined
      if (languageKnowledge.originalWorkAreas) {
        originalWorkArea = find(pathEq(['workArea', 'id'], workArea), languageKnowledge.originalWorkAreas)
      }
      return {
        id: originalWorkArea ? originalWorkArea.id : undefined,
        sourceLanguage: languageKnowledge.sourceLanguage,
        workArea: workArea
      }
    },
    languageKnowledge.workArea
  )

const flattenLanguageKnowledges = compose(
  flatten,
  map(
    formatLanguageKnowledge
  )
)

const prepareFormatFormDataForAPI = (termsList) => evolve({
  languageKnowledges: val => val ? flattenLanguageKnowledges(val) : [],
  terms: val => val.length === termsList.length ? true : false
})


const renderButtons = (step, gotoStepAfterSubmit, gotoStep, isVatIdUnverified, onboarded) => {
  const NextButton = ({step}) => onboarded
    ? <button type="submit" className="btn btn-success">Update</button>
    : <button type="submit" className="btn btn-success" onClick={gotoStepAfterSubmit(step)}>Next</button>
  const BackButton = ({step}) => onboarded
    ? null
    : <span className="btn btn-success" onClick={gotoStep(step)}>back</span>

  const { personal, languages, terms, company } = validSteps


  switch (step) {
    case personal:
      return <span className='float-right'>
                  <NextButton step={languages}/>
             </span>
      break
    case languages:
      return <span className='float-right'>
                    <BackButton step={personal}/>
                    <NextButton step={onboarded ? company : terms}/>
             </span>
      break
    case terms:
      return <span className='float-right'>
                    <BackButton step={languages} />
                    <button type="submit" className="btn btn-success">Submit</button>
             </span>
      break
    case company:
      return <span className='float-right'>
                    <BackButton step={languages} />
                    <NextButton />
                    </span>
      break
  }
}

const Wizard = ({step, onboarded, setStep}) => {
  const { personal, languages, terms, company } = validSteps
  const WizardItem = ({ itemStep, children }) => <li key={itemStep} onClick={setStep(itemStep)} className={`${step == itemStep ? 'active' : ''}`}>{children}</li>

  return <ol className='wizard manage-margin'>
    <WizardItem key={personal} itemStep={personal}>Personal</WizardItem>
    <WizardItem key={languages} itemStep={languages}>Languages</WizardItem>
    {!onboarded
      ? <WizardItem key={terms} itemStep={terms}>Terms</WizardItem>
      : [
        <WizardItem key={company} itemStep={company}>Company</WizardItem>,
      ]
    }
  </ol>
}

const Welcome = ({step}) => {
  let text
  switch (step) {
    case validSteps.personal:
      text = <p key={validSteps.personal} className="body-text">
        Hi,<br/>
        Great to see you. We’re really happy you want to be a part of the family.<br/>
        We wouldn’t have a business without you, so we’re committed to be an attractive partner.<br/>
        We’ll give your <b>fair pay</b> for your great work, <b>easy payment</b> and <b>a minimum of admin work</b> – that leaves you to do what
        you
        do best.<br/>
        Just complete the signup form and you’re up and running in no time.<br/>
        Let us know if you have any questions.<br/>
      </p>
      break
    case validSteps.languages:
      text = <p key={validSteps.languages} className="body-text">
        Now to the important stuff – your skills.<br/>
        Make sure your profile is up to date, to maximise the chance of winning jobs.
      </p>
      break
    case validSteps.company:
      text = <p key={validSteps.company} className="body-text">
        We need a few details to be able to send you payment.<br/>
        Please note that a valid vat Id is required.
      </p>
      break
    default:
      return null
  }
  return <div className="welcome-text">{text}</div>
}

const SpecialistDetails = ({intl, checkProfileStatus, step, setSpecialistDetailsStep: setStep, location}) => {
  // set step from route on first render
  useEffect(() => { location.state ? setStep(location.state.step) : null }, [])

  const [isVatIdUnverified, setVatIdUnverified] = useState(true)
  const formatFormDataForAPI = prepareFormatFormDataForAPI(getTermsList(intl))
  let nextStep = null
  const nextStepCallback = (next) => () => nextStep = next
  let formRef
  const setStepCallback = (step) => async () => {
    await getConfiguredClient().mutate({
      mutation: addOrUpdate,
      variables: formatFormDataForAPI(formRef.state.formData)
    })
    return setStep(step)
  }

  return <Query query={query}>{
    ({loading, error, data}) => {
      debug('in query')
      if (loading) return <LoadingView/>
      if (error) return <GraphqlError error={error}/>

      setVatIdUnverified(
        data.specialist === null
        || data.specialist.vatIdVerification === 'unverified'
        || data.specialist.vatIdVerification === 'invalid'
      )

      return <Mutation
        mutation={addOrUpdate}
        refetchQueries={() => {
          if (!data.specialist) {
            return [{query: query}]
          }
          return []
        }}
        awaitRefetchQueries={true}
        onCompleted={(data) => {
          if (data.addOrUpdateSpecialist && nextStep) {
            setStep(nextStep)
            nextStep = null
          }
          checkProfileStatus()
        }}
      >
        {(updateSpecialist, {loading, error}) => {
          debug('in mutation')
          let currentData = undefined
          if (!data.specialist) {
            if(loading) {
              debug('no specialist, waiting for loading to be done')
              return <LoadingView/>
            } else {
              debug('creating new specialist')
              updateSpecialist()
              return null
            }
          }

          const isVatIdInvalid = prop('vatIdVerification', data.specialist) === 'invalid'
          const profileStatus = prop('profileStatus', data.specialist)
          const isProfileActive = profileStatus === 'active'
          const isProfileOnboarded = profileStatus === 'onboarded' || isProfileActive || profileStatus === 'disabled'

          // make sure we do not end up on an invalid state
          if(isProfileOnboarded && step === validSteps.terms
            || !isProfileOnboarded && includes(step, [validSteps.company, validSteps.payment])
          ) { setStep(validSteps.personal)}

          return <CardLayout columns={12}>
            <div className="card-header bg-white">
              <Wizard step={step} onboarded={isProfileOnboarded} setStep={setStepCallback}/>
            </div>
            <div className="card-body">
              {!isProfileOnboarded ? <Welcome step={step}/> : null}
              {error ? <GraphqlError error={error}/> : null}
              <Form
                ref={(form) => formRef = form}
                schema={getSchema(intl, data.countryCodes, data.motherTongues, data.sourceLanguages, data.workAreas, step, isVatIdUnverified)}
                formContext={intl}
                formData={formatSpecialistForForm(data.specialist, intl)}
                uiSchema={uiSchema(step, isVatIdUnverified, isVatIdInvalid, loading, isProfileActive, data.specialist.canUseStripe, intl)}
                fields={{
                  layout_grid: LayoutField,
                  TitleField: TitleField
                }}
                FieldTemplate={FieldTemplate}
                onSubmit={data => {
                  const formattedData = formatFormDataForAPI(data.formData)
                  currentData = data.formData
                  return updateSpecialist({
                    variables: formattedData
                  })
                }}
                liveValidate={true}
                showErrorList={false}
                validate={validate(intl, nextStep && !error ? nextStep : step)}

              >{renderButtons(step, nextStepCallback, setStepCallback, isVatIdUnverified, isProfileOnboarded)}
              </Form>
            </div>
          </CardLayout>
        }}</Mutation>
    }
  }</Query>
}

const mapStateToProps = ({components}) => ({step: components.specialistDetails.step})

export default connect(mapStateToProps, {checkProfileStatus, setSpecialistDetailsStep})(
  withRouter(injectIntl(SpecialistDetails))
)



// WEBPACK FOOTER //
// ./Linkgua/components/SpecialistDetails/index.js